/**
 * The Survey Map, connected: the docked bar, the sheet, and the toast, wired
 * to the one survey the shell has open (D-028).
 *
 * CONNECTED, like `QuestionList`: it reads `useSurvey()` and the device's
 * walked trail, derives the tree through `mapModel()` on every document
 * change, and owns three pieces of UI state that are not survey data —
 * whether the sheet is open, which room branches are collapsed, and whether
 * this survey's map has been "born" yet.
 *
 * ── Setup gives birth to the tree ───────────────────────────────────────────
 * Brief §2: *"The moment rooms/cars/groups/hoistways are set, the map exists
 * and is complete — first open after setup cascades the rows in."* The first
 * time the sheet opens on a survey whose declared rooms are all set up, the
 * top-level nodes cascade in; after that, never again for that survey. Held in
 * a module-level set, so a re-mount on navigation does not replay it, and NOT
 * persisted — a fresh session is allowed one more cascade.
 *
 * ── Collapse state persists per survey while it is open ─────────────────────
 * The same: a module map keyed by survey id, session-lived. The default is
 * the room you are standing in expanded and every other room collapsed; a
 * survey with no rooms yet has nothing to collapse.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { useRoute } from '../useRoute';
import { mapModel, type MapRowModel } from '../state/mapTree';
import { useSurvey } from '../state/SurveyProvider';
import { summarize } from '../state/surveyList';
import { useWalkedSections } from '../state/walked';
import { settleViewport } from '../state/viewportGlue';
import { MapBar } from './MapBar';
import { MapSheet } from './MapSheet';
import { Toast } from './Toast';

/** Surveys whose map has already cascaded in this session. */
const born = new Set<string>();
/** Per-survey collapse state, session-lived. */
const collapsed = new Map<string, Set<string>>();

const TOAST_MS = 1700;

export function SurveyMap(): ReactNode {
  const { surveyId, doc, results, record } = useSurvey();
  const route = useRoute();
  const walked = useWalkedSections(surveyId);
  const barRef = useRef<HTMLButtonElement | null>(null);

  const model = useMemo(
    () =>
      mapModel({ doc, results, walked, surveyTitle: summarize(record).title, route }),
    [doc, results, walked, record, route],
  );

  const [open, setOpen] = useState(false);
  const [bornNow, setBornNow] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // Bumped on every branch toggle: the collapse set lives in a module map, so
  // the memo below has to be told it changed.
  const [version, bump] = useState(0);

  // Which room the surveyor is standing in decides the default collapse set.
  const currentRoomId = useMemo(() => {
    const key = model.currentKey;
    if (!key) return undefined;
    return results.sectionById.get(key)?.scope.roomId;
  }, [model.currentKey, results]);

  const openBranches = useMemo(() => {
    const closed = collapsed.get(surveyId);
    const all = doc.rooms.map((room) => room.id);
    if (!closed) {
      // First look: the room you are in open, the rest collapsed.
      return new Set(all.filter((id) => currentRoomId === undefined || id === currentRoomId));
    }
    return new Set(all.filter((id) => !closed.has(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `version` is the module map's clock
  }, [surveyId, doc.rooms, currentRoomId, version]);

  const onToggleBranch = useCallback(
    (roomId: string): void => {
      let closed = collapsed.get(surveyId);
      if (!closed) {
        // Materialise the default before the first toggle so a later render
        // does not recompute it around the surveyor's choice.
        closed = new Set(
          doc.rooms.map((r) => r.id).filter((id) => currentRoomId !== undefined && id !== currentRoomId),
        );
        collapsed.set(surveyId, closed);
      }
      if (closed.has(roomId)) closed.delete(roomId);
      else closed.add(roomId);
      bump((n) => n + 1);
    },
    [surveyId, doc.rooms, currentRoomId],
  );

  const setupComplete = useMemo(() => {
    if (doc.rooms.length === 0) return false;
    return doc.rooms.every(
      (r) => r.cars.length > 0 && r.groups.length > 0 && r.hoistways.length > 0,
    );
  }, [doc.rooms]);

  const openMap = useCallback((): void => {
    // The sheet is `bottom: 0` fixed; if the keyboard was just up (typing a
    // section note, say) the layout viewport can still be stale (round 8
    // task 2, the field bug), so settle it before the sheet's own transform
    // starts sliding in.
    settleViewport();
    if (setupComplete && !born.has(surveyId)) {
      born.add(surveyId);
      setBornNow(true);
      window.setTimeout(() => setBornNow(false), 1200);
    }
    setOpen(true);
  }, [setupComplete, surveyId]);

  const closeMap = useCallback((): void => {
    setOpen(false);
  }, []);

  const say = useCallback((message: string): void => {
    setToast(message);
  }, []);

  useEffect(() => {
    if (toast === null) return undefined;
    const t = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => window.clearTimeout(t);
  }, [toast]);

  const onNavigate = useCallback(
    (row: MapRowModel): void => {
      closeMap();
      say(row.current ? `Already on ${row.title}` : `Jumped to ${row.title}`);
    },
    [closeMap, say],
  );

  // Swipe up on the bar opens the sheet.
  const touchY = useRef<number | null>(null);

  return (
    <>
      <MapBar
        model={model}
        onOpen={openMap}
        onTouchStart={(event) => {
          touchY.current = event.touches[0]?.clientY ?? null;
        }}
        onTouchMove={(event) => {
          const y = event.touches[0]?.clientY;
          if (touchY.current !== null && y !== undefined && touchY.current - y > 24) {
            touchY.current = null;
            openMap();
          }
        }}
      />
      <span ref={barRef} hidden />
      <MapSheet
        model={model}
        open={open}
        born={bornNow}
        openBranches={openBranches}
        onToggleBranch={onToggleBranch}
        onClose={closeMap}
        onNavigate={onNavigate}
      />
      <Toast message={toast} />
    </>
  );
}
